import {
  Box,
  Button,
  Flex,
  Image,
  Skeleton,
  Text,
  Tooltip,
  useMediaQuery,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import Expand from 'assets/expand_icon.png';
import HelpComponent from 'components/CustomHelp';
import ArrowLeftBlocks from 'components/Icons/ArrowLeftBlocks';
import Cross from 'components/Icons/Cross';
import transformDimensions from 'connectors/transformDimensions';
import ErrorPage from 'pages/ErrorPage';
import { STATUS } from 'projectConstants';
import {
  fetchDimensionItems,
  fetchModelTime,
  geModelTimeStatus,
  getIndicatorDetail,
  getIndicatorDetailStatus,
  getModelTime,
  getTableShimmer,
  resetIndicator,
} from 'redux/IndicatorPageSlice';
import { getCurrentDimensionIndex } from 'redux/InputPageSlice';
import {
  closeInputSection,
  fetchBlockDrivers,
  FetchPlangPageIndicatorTableData,
  getAppliedFilters,
  getBlockDimensions,
  getIndicatorTableData,
  getIndicatorTableDataStatus,
  getIndicatorTableDataError,
  getShowNonEditableColumns,
  getSelectedDriver,
  onErrorShow,
  planPageShow,
  toggleShowNonEditableColumns,
  toggleExpand,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import QuestionMarkIcon from 'components/Icons/QuestionMarkIcon';
import { AppDispatch } from 'utils/GlobalHelpers';
import ErrorBoundary from 'components/ErrorBoundary';
import { shouldRefetchPlanPageV2OnInputEdit } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageFetchHelpers';
import {
  fetchPlanPageIndicatorChartDetailsNow,
  resetPlanPageIndicatorChartFetchScheduler,
  resolveIndicatorChartDimId,
  schedulePlanPageIndicatorChartDetailsFetch,
} from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageIndicatorChartFetch';
import { syncPlanFiltersToIndicatorInputPanel } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageInputPanelFilters';

import Chart from './Chart';
import IndicatorTable from './IndicatorTable';
import InputTypeGroupedButton from './InputTypeGroupedButton';
import MultiDimSetting from './MultiDimSetting';
import { monthNames } from './MultiDimensionTableOpzV2.utils';

import type { PlanPageRefetchRequest } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planPageFetchHelpers';

export interface Properties {
  refetchRequest: PlanPageRefetchRequest;
}

const isHeaderNonEditable = (header: any, date?: string): boolean => {
  if (header.is_dimension) return false;
  if (!date) return false;

  if (header.name.startsWith('FY')) {
    const scenarioYear = Number(date);
    if (!Number.isFinite(scenarioYear)) return false;
    return Number(header.name.slice(2, 4)) + 2000 < scenarioYear;
  }

  const headingParts = String(header.name).split('-');
  const scenarioParts = date.split('-');
  if (headingParts.length < 2 || scenarioParts.length < 2) return false;

  const headingYear = Number(headingParts[1]);
  const scenarioYear = Number(scenarioParts[1]);
  if (!Number.isFinite(headingYear) || !Number.isFinite(scenarioYear)) return false;

  if (headingYear < scenarioYear) return true;
  return (
    headingYear === scenarioYear &&
    monthNames.indexOf(headingParts[0]) <= monthNames.indexOf(scenarioParts[0])
  );
};

const ActualsToggleButton = ({ isVisible, isShowing, onClick }: any) => {
  if (!isVisible) return null;

  return (
    <button
      type='button'
      onClick={onClick}
      style={{
        border: '1px solid #CBD5E0',
        borderRadius: 6,
        padding: '3px 10px',
        background: 'transparent',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        marginRight: 10,
      }}
    >
      {isShowing ? 'Hide actuals' : 'Show actuals'}
    </button>
  );
};

const IndicatorPageMultiDim = ({ refetchRequest }: Properties) => {
  const { pathname } = useLocation();
  const { modelId, blockId } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const containerReference = useRef<HTMLDivElement>(null);

  const [innerHeight, setInnerHeight] = useState(0);
  const [noDimChartData, setNoDimChartData] = useState<Record<string, Record<string, number>> | []>(
    [],
  );
  const currentDriver = useSelector(getSelectedDriver);
  const appliedFilters = useSelector(getAppliedFilters);
  const blockDimensions = useSelector(getBlockDimensions);

  const indicator = useSelector(getIndicatorDetail);
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const tabShimmer = useSelector(getTableShimmer);
  const modelTimeStatus = useSelector(geModelTimeStatus);
  const modelTime = useSelector(getModelTime);
  const currentDimensionIndex = useSelector(getCurrentDimensionIndex);
  const showPlanPage = useSelector(planPageShow);
  const baseScenario = useSelector(getBaseScenario);
  const tableData = useSelector(getIndicatorTableData);
  const tableDataStatus = useSelector(getIndicatorTableDataStatus);
  const tableDataError = useSelector(getIndicatorTableDataError);
  const showNonEditableColumns = useSelector(getShowNonEditableColumns);

  const [isMobile] = useMediaQuery('(max-width: 1024px)');
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const dimensionId = currentDriver?.dimensions[currentDimensionIndex];
  const tooltipReference = useRef<any>(null);

  const tableReady =
    tableDataStatus === STATUS.SUCCESS &&
    Number(tableData?.indicator_id) === Number(currentDriver?.id) &&
    Boolean(tableData?.column_headers?.length);

  const showChartShimmer = indicatorDetailStatus === STATUS.LOADING || tabShimmer === true;

  /**
   * After input-panel edits refresh the input chart via /details always.
   * Plan-page /v2 runs only when the main plan chart/table are visible (split view).
   */
  const refreshPlanAndInputChart = useCallback<PlanPageRefetchRequest>(
    (options) => {
      if (shouldRefetchPlanPageV2OnInputEdit(showPlanPage, currentDriver)) {
        refetchRequest({ ...options, force: true });
      }

      if (!currentDriver?.id || !baseScenario?.id) {
        return;
      }

      fetchPlanPageIndicatorChartDetailsNow({
        dispatch,
        currentDriver,
        scenarioId: baseScenario.id,
        dimId: resolveIndicatorChartDimId(currentDriver, tableData),
        appliedFilters,
        blockDimensions,
      });
    },
    [
      appliedFilters,
      baseScenario?.id,
      blockDimensions,
      currentDriver,
      dispatch,
      refetchRequest,
      showPlanPage,
      tableData,
    ],
  );

  const hasAnyNonEditableValueColumns = useMemo(() => {
    if (!tableData?.column_headers?.length) return false;

    const startDate = new Date(baseScenario?.start_date);
    startDate.setMonth(startDate.getMonth() - 1);
    const yearFormat = startDate.getFullYear();
    const monthFormat = `${monthNames[startDate.getMonth()]}-${String(yearFormat).slice(-2)}`;
    const targetDate = tableData?.type === 'raw' ? monthFormat : String(yearFormat);

    return tableData.column_headers.some((header: any) => isHeaderNonEditable(header, targetDate));
  }, [tableData?.column_headers, tableData?.type, baseScenario?.start_date]);

  useOutsideClick({
    ref: tooltipReference,

    handler: () => {
      setIsTooltipOpen(false);
    },
  });

  useLayoutEffect(() => {
    if (containerReference?.current) {
      const containerHeight = containerReference.current.offsetHeight;
      const containerPadding =
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingLeft, 10) +
        Number.parseInt(window.getComputedStyle(containerReference.current).paddingRight, 10);
      const heightWithoutPadding = containerHeight - containerPadding;
      setInnerHeight(heightWithoutPadding);
    }
  }, [containerReference?.current]);

  const fetchModelTimeData = () => dispatch(fetchModelTime(Number(modelId)));

  const fetchDimItemsData = () => {
    dispatch(
      fetchDimensionItems({
        dimensionId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  useEffect(() => {
    if (modelId && blockId) {
      fetchModelTimeData();
    }
  }, [pathname, currentDriver]);

  useEffect(() => {
    if (currentDriver && currentDriver?.dimensions?.length > 0) {
      fetchDimItemsData();
    }
  }, [pathname, currentDriver?.id, baseScenario?.id, dimensionId]);

  useEffect(() => {
    if (currentDriver?.id) {
      resetPlanPageIndicatorChartFetchScheduler();
      dispatch(resetIndicator());
    }
  }, [currentDriver?.id, dispatch]);

  useEffect(() => {
    if (currentDriver && baseScenario) {
      dispatch(
        FetchPlangPageIndicatorTableData({
          indicator_id: currentDriver?.id,
          parameters: {
            scenario_id: baseScenario?.id,
            format_excel_data: true,
          },
        }),
      );
    }
  }, [currentDriver, baseScenario?.id]);

  useEffect(() => {
    if (
      tableDataStatus !== STATUS.SUCCESS ||
      !tableData?.dimensions?.length ||
      !currentDriver?.id ||
      Number(tableData.indicator_id) !== Number(currentDriver.id)
    ) {
      return;
    }

    syncPlanFiltersToIndicatorInputPanel(dispatch, {
      appliedFilters,
      blockDimensions,
      indicatorDimensions: tableData.dimensions,
    });
  }, [
    appliedFilters,
    blockDimensions,
    currentDriver?.id,
    dispatch,
    tableData?.dimensions,
    tableData?.indicator_id,
    tableDataStatus,
  ]);

  // Chart /details in parallel with /data_input — do not wait for table payload.
  useEffect(() => {
    if (!currentDriver?.id || !baseScenario?.id) {
      return;
    }

    fetchPlanPageIndicatorChartDetailsNow({
      dispatch,
      currentDriver,
      scenarioId: baseScenario.id,
      dimId: resolveIndicatorChartDimId(currentDriver, tableData),
      appliedFilters,
      blockDimensions,
    });
  }, [currentDriver?.id, baseScenario?.id, dispatch]);

  // Debounced refetch when plan filters or table headers refine dim_id / filter payload.
  useEffect(() => {
    if (!currentDriver?.id || !baseScenario?.id) {
      return;
    }

    schedulePlanPageIndicatorChartDetailsFetch({
      dispatch,
      currentDriver,
      scenarioId: baseScenario.id,
      dimId: resolveIndicatorChartDimId(currentDriver, tableData),
      appliedFilters,
      blockDimensions,
    });
  }, [appliedFilters, baseScenario?.id, blockDimensions, currentDriver, dispatch, tableData]);

  useEffect(() => {
    if (indicator && !indicator?.output?.dims) {
      const object: any = {};
      object[indicator.output?.name] = indicator.output?.total_values;
      setNoDimChartData(object);
    }
  }, [indicator]);

  useEffect(() => {
    if (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) {
      dispatch(onErrorShow(true));
    }
  }, [indicatorDetailStatus, modelTimeStatus]);

  const toggle = () => dispatch(toggleExpand());

  const closeSection = () => {
    dispatch(closeInputSection());

    if (!showPlanPage) {
      refetchRequest({ force: true });
      toggle();
      return;
    }

    refreshPlanAndInputChart();
  };

  if (
    (indicatorDetailStatus === STATUS.FAILED || modelTimeStatus === STATUS.FAILED) &&
    !tableReady
  ) {
    return (
      <>
        {isMobile && (
          <Flex
            justifyContent={'space-between'}
            alignItems={'center'}
            p={4}
            backgroundColor={'transparent'}
          >
            <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
              {isMobile && (
                <Box mr='6px' fontFamily={'regular_roboto'}>
                  <ArrowLeftBlocks />
                </Box>
              )}
              {isMobile ? (
                <Text size='assistant_md'>Inputs</Text>
              ) : (
                <Flex alignItems={'center'}>
                  <Text ml={3} size='assistant_md'>
                    {indicator?.output?.name}
                  </Text>
                </Flex>
              )}
            </Flex>
            <Cross height={'25px'} width={'25px'} onClick={closeSection} />
          </Flex>
        )}
        <Flex alignItems={'center'} justifyContent={'center'} height={'inherit'}>
          <ErrorPage planError={true} />
        </Flex>
      </>
    );
  }
  const refetchDrivers = async () => {
    await dispatch(
      fetchBlockDrivers({
        blockId,
        parameters: {
          scenario_id: baseScenario?.id,
        },
      }),
    );
  };

  // Show ErrorBoundary fallback if there's an API error
  if (tableDataStatus === STATUS.FAILED) {
    return (
      <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
        <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
          Failed to Load Indicator Data
        </Text>
        <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
          {tableDataError || 'Unable to fetch indicator table data. Please try again.'}
        </Text>
        <Button
          size='md'
          colorScheme='blue'
          onClick={() => {
            if (currentDriver && baseScenario) {
              dispatch(
                FetchPlangPageIndicatorTableData({
                  indicator_id: currentDriver?.id,
                  parameters: {
                    scenario_id: baseScenario?.id,
                    format_excel_data: true,
                  },
                }),
              );
            }
          }}
        >
          Retry
        </Button>
      </Flex>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Flex direction='column' align='center' justify='center' h='100vh' p={6}>
          <Text fontSize='xl' fontWeight='bold' color='red.600' mb={4}>
            Something went wrong with the Indicator Page
          </Text>
          <Text fontSize='md' color='gray.600' textAlign='center' maxW='500px' mb={6}>
            {tableDataError ||
              'We encountered an unexpected error while loading the indicator data. This might be due to a network issue or a temporary problem with the data source.'}
          </Text>
          <Button
            size='md'
            colorScheme='blue'
            onClick={() => {
              if (currentDriver && baseScenario) {
                dispatch(
                  FetchPlangPageIndicatorTableData({
                    indicator_id: currentDriver?.id,
                    parameters: {
                      scenario_id: baseScenario?.id,
                      format_excel_data: true,
                    },
                  }),
                );
              } else {
                window.location.reload();
              }
            }}
          >
            {tableDataError ? 'Retry' : 'Reload Page'}
          </Button>
        </Flex>
      }
    >
      <Flex
        display='flex'
        flexDirection='column'
        className='scroll'
        h='100vh'
        width='100%'
        p={2}
        pb='0 !important'
      >
        <Flex justifyContent='space-between' alignItems='center'>
          <Flex onClick={closeSection} alignItems='center' cursor='pointer'>
            {isMobile && (
              <Box mr='6px' fontFamily='regular_roboto'>
                <ArrowLeftBlocks />
              </Box>
            )}
            {isMobile ? (
              <Text size='assistant_md'>Inputs</Text>
            ) : (
              <Flex
                alignItems='center'
                gap='2'
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Text ml={3} size='assistant_md'>
                  {indicator?.output?.name}
                </Text>
                {indicator?.help && (
                  <Tooltip
                    isOpen={isTooltipOpen}
                    hasArrow
                    arrowSize={10}
                    bg='white'
                    placement='right'
                    boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
                    label={
                      <VStack
                        bg='white'
                        h='inherit'
                        p='3'
                        justify='space-between'
                        maxWidth='300px'
                        width='180px'
                        alignItems='center'
                        alignContent='center'
                        gap={1}
                        zIndex={1}
                      >
                        <Text
                          mb='2.5'
                          fontFamily='bold_roboto'
                          fontSize='sm'
                          lineHeight='4'
                          noOfLines={2}
                          color='black'
                        >
                          Help
                        </Text>
                        <Text
                          size='modalContent'
                          fontFamily='regular_roboto'
                          noOfLines={3}
                          color='black'
                          fontSize='2xs'
                        >
                          {indicator?.help}
                        </Text>
                      </VStack>
                    }
                    borderRadius='4'
                    width='-moz-min-content'
                  >
                    <Box
                      onMouseEnter={() => setIsTooltipOpen(true)}
                      onMouseLeave={() => setIsTooltipOpen(false)}
                      ref={tooltipReference}
                    >
                      <QuestionMarkIcon height={23} width={23} color='white' />
                    </Box>
                  </Tooltip>
                )}
              </Flex>
            )}
          </Flex>
          <Cross height='25px' width='25px' onClick={closeSection} />
        </Flex>
        {isMobile && (
          <Flex alignItems={'center'} mt={1}>
            <Text ml={3} size='assistant_md'>
              {indicator?.output?.name}
            </Text>
          </Flex>
        )}
        {showChartShimmer && !indicator && (
          <Box
            h={innerHeight ? `${innerHeight}px` : '30vh'}
            pt={4}
            minH={innerHeight ? `${innerHeight}px` : '30vh'}
            mb='3'
          >
            <Skeleton startColor='white' endColor='#CCD4E8' h={'-webkit-fill-available'} />
          </Box>
        )}

        {indicator && (
          <Box display={{ base: 'block' }} ref={containerReference} height={'25vh'}>
            {indicator?.output?.dims ? (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output.total_values}
                chartData={indicator?.output?.dim_item_data}
                chartType={indicator?.output?.chart_type}
                dimId={indicator?.output?.dims?.id}
                formatProperties={indicator?.output?.data_format_properties}
              />
            ) : (
              <Chart
                showLabel={true}
                chartLabelData={indicator?.output?.total_values}
                chartData={noDimChartData}
                chartType={indicator?.output?.chart_type}
                formatProperties={indicator?.output?.data_format_properties}
              />
            )}
          </Box>
        )}

        {/* flex='1' + minHeight={0}: without an explicit height, this was
            sizing to its content inside the h='100vh' parent — a flex
            item's default min-height is `auto`, not 0, so it refused to
            shrink below the table's full row count. That let the table
            grow past the viewport bottom instead of scrolling inside its
            own overflowY='scroll' box below, since that box's maxH='100%'
            had no bounded parent height to resolve against. Taking the
            remaining space explicitly here gives the table a real ceiling
            to scroll within — the amount of headroom this needs varies
            with viewport height and row count, which is why it only
            showed up on some screens/datasets and not others. */}
        <Flex
          direction='column'
          borderTopLeftRadius='base'
          borderTopRightRadius='base'
          flex='1'
          minHeight={0}
        >
          {!showPlanPage && (
            <Flex alignItems={'center'} justifyContent={'space-between'} w={'100%'}>
              <Flex w={'100%'} justifyContent={'space-between'}>
                <Flex w={'60%'}>
                  <Box width={'70%'} maxWidth={'450px'} alignItems={'center'}>
                    {currentDriver.dimensions && (
                      <InputTypeGroupedButton
                        inputDimensions={transformDimensions(currentDriver.dimensions)}
                        blockId={currentDriver.block_id}
                        inputType={tableData?.type}
                        indicatorId={indicator?.id}
                        refetchDrivers={refetchDrivers}
                        inputDataValues={tableData?.data_values}
                        timeDimension={currentDriver.time_dim}
                        timeRange={modelTime?.time_range}
                        timeRangeYears={modelTime?.time_range_years}
                        refetchRequest={refreshPlanAndInputChart}
                      />
                    )}
                  </Box>
                  <Flex w={'30%'} align={'center'} ps={3}>
                    <MultiDimSetting
                      indicatorId={indicator?.id}
                      refetchRequest={refreshPlanAndInputChart}
                    />
                  </Flex>
                </Flex>
                <VStack>
                  <Box
                    display={'flex'}
                    width='100%'
                    justifyContent={'end'}
                    alignItems={'center'}
                    cursor={'pointer'}
                    onClick={toggle}
                  >
                    <ActualsToggleButton
                      isVisible={hasAnyNonEditableValueColumns}
                      isShowing={showNonEditableColumns}
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        event.stopPropagation();
                        dispatch(toggleShowNonEditableColumns());
                      }}
                    />
                    <Text fontSize={'xs'}>Collapse view</Text>
                    <Image height={'1.5rem'} ml={1} src={Expand} />
                  </Box>
                  <Box>
                    <Text fontSize={'3xs'}>Click and drag cells to select multiple periods</Text>
                  </Box>
                </VStack>
              </Flex>
            </Flex>
          )}
          {showPlanPage && (
            <>
              <Box>
                {currentDriver?.dimensions && (
                  <InputTypeGroupedButton
                    inputDimensions={transformDimensions(currentDriver.dimensions)}
                    blockId={currentDriver.block_id}
                    inputType={tableData?.type}
                    indicatorId={indicator?.id}
                    refetchDrivers={refetchDrivers}
                    inputDataValues={tableData?.data_values}
                    timeDimension={currentDriver.time_dim}
                    timeRange={modelTime?.time_range}
                    timeRangeYears={modelTime?.time_range_years}
                    refetchRequest={refreshPlanAndInputChart}
                  />
                )}
              </Box>
              {isMobile && (
                <Flex align={'center'} gap={2}>
                  <ActualsToggleButton
                    isVisible={hasAnyNonEditableValueColumns}
                    isShowing={showNonEditableColumns}
                    onClick={() => dispatch(toggleShowNonEditableColumns())}
                  />
                  <MultiDimSetting
                    indicatorId={indicator?.id}
                    refetchRequest={refreshPlanAndInputChart}
                  />
                </Flex>
              )}
              <Box
                display={{ base: 'none', md: 'flex' }}
                width='100%'
                justifyContent={'space-between'}
                alignItems={'center'}
                cursor={'pointer'}
              >
                <Flex align={'center'} gap={2}>
                  <MultiDimSetting
                    indicatorId={indicator?.id}
                    refetchRequest={refreshPlanAndInputChart}
                  />
                </Flex>
                <Flex align={'center'}>
                  <ActualsToggleButton
                    isVisible={hasAnyNonEditableValueColumns}
                    isShowing={showNonEditableColumns}
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      dispatch(toggleShowNonEditableColumns());
                    }}
                  />
                  <Text onClick={toggle} fontSize={'xs'}>
                    Expand view
                  </Text>
                  <Image onClick={toggle} height={'1.5rem'} ml={1} src={Expand} />
                </Flex>
              </Box>
            </>
          )}

          {isMobile ? (
            <>
              <Box mt='1' mb='1' className='sm-scroll'>
                <IndicatorTable
                  refetchRequest={refreshPlanAndInputChart}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
              </Box>
              {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent helpText={indicator?.help} style={{ width: '100%' }} />
                </Box>
              )}
            </>
          ) : (
            <>
              <Box overflowY='scroll' className='scroll' maxH={'100%'}>
                <IndicatorTable
                  refetchRequest={refreshPlanAndInputChart}
                  isPercentage={indicator?.data_format.toLowerCase()}
                />
                {/* {indicator?.help && (
                <Box mt={5} pb={3}>
                  <HelpComponent
                    helpText={indicator?.help}
                    style={{ width: { base: '98%', '8xl': '380px' } }}
                  />
                </Box>
              )} */}
              </Box>
            </>
          )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

export default IndicatorPageMultiDim;
